import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import Slider from "./Slider";
import CompleteDropdown from "../../../../shared/Dropdown/CompleteDropdown";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import crudApi from "@serie3/common/api/crudApi";
import { useEffect, useState, useCallback } from "react";
import listHandler from "@serie3/common/Domain/listHandler";
import AddButton from "../../../../shared/Buttons/AddButton";
import { Col, Row } from "react-bootstrap";
import Interactions from "../../../../shared/Interactions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy, faArrowTrendDown } from "@fortawesome/free-solid-svg-icons";

const PILLAR_COLORS = [
  "#038edc",
  "#51d28c",
  "#f1734f",
  "#564ab1",
  "#f7cc53",
  "#5fd0f3",
];

const ContentPillarTimeline = ({
  pillar,
  pillars,
  setPillars,
  index,
  editAction,
  setEditPostList,
  setShowPostForm,
  setEditPost,
  trigger,
  onStatsUpdate,
  badges = {},
}) => {
  const { t } = useTranslation();
  const color = PILLAR_COLORS[index % PILLAR_COLORS.length];

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [averageScore, setAverageScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [stats, setStats] = useState({ views: 0, likes: 0, comments: 0 });
  const [bestPost, setBestPost] = useState(null);
  const [worstPost, setWorstPost] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoadingPosts(true);
      await crudApi.getAll(
        SocialEndpoints.posts.contentPillars.showposts + pillar.id,
        setPosts,
        "posts"
      );
      setLoadingPosts(false);
    };
    fetchPosts();
  }, [trigger, pillar.id]);

  useEffect(() => {
    let totalViews = 0, totalLikes = 0, totalComments = 0;
    posts.forEach((post) => {
      if (post.stats?.length > 0) {
        totalViews    += parseInt(post.stats.at(-1).views,    10) || 0;
        totalLikes    += parseInt(post.stats.at(-1).likes,    10) || 0;
        totalComments += parseInt(post.stats.at(-1).comments, 10) || 0;
      }
    });
    setStats({ views: totalViews, likes: totalLikes, comments: totalComments });

    const postScore = (p) => Number(p.stats?.at(-1)?.score) || 0;
    const postsWithScore = posts.filter((p) => postScore(p) > 0);
    const sum = postsWithScore.reduce((acc, p) => acc + postScore(p), 0);
    const avg = postsWithScore.length > 0 ? sum / postsWithScore.length : 0;
    setTotalScore(Math.round(sum));
    setAverageScore(Math.round(avg));
    onStatsUpdate?.(pillar.id, { totalScore: Math.round(avg) });

    if (postsWithScore.length > 0) {
      setBestPost(postsWithScore.reduce((a, b) => postScore(a) >= postScore(b) ? a : b));
      setWorstPost(postsWithScore.reduce((a, b) => postScore(a) <= postScore(b) ? a : b));
    } else {
      setBestPost(null);
      setWorstPost(null);
    }
  }, [posts]);

  const deleteAction = () => {
    listHandler.deleteFromList(
      index,
      crudApi.del(SocialEndpoints.posts.contentPillars.delete, pillar.id),
      setPillars,
      pillars
    );
  };

  return (
    <div className="row mb-4">
      <div className="col-lg-12">
        <div
          className="card"
          style={{ borderLeft: `4px solid ${color}`, borderRadius: "16px" }}
        >
          <div className="card-body" style={{ padding: "20px 24px" }}>
            {/* Header */}
            <div className="d-flex align-items-center mb-3">
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: color,
                  marginRight: 10,
                  flexShrink: 0,
                }}
              />
              <h5
                className="card-title mb-0 flex-grow-1"
                style={{ fontWeight: 700, fontSize: 16, color: "#1a2236" }}
              >
                {pillar.pillar}
              </h5>
              <span
                style={{
                  fontSize: 12,
                  color: "#74788d",
                  marginRight: 12,
                }}
              >
                {posts.length} {t("Backend.Post.ContentPillars.Posts", "posts")}
              </span>
              <div className="d-flex gap-2 align-items-center swiper-arrow arrow-sm">
                <div
                  className={`swiper-button-prev prev-${pillar.id} prev position-relative rounded-start`}
                />
                <div
                  className={`swiper-button-next next-${pillar.id} next position-relative rounded-end`}
                />
                <CompleteDropdown
                  editAction={() => editAction(pillar)}
                  deleteAction={posts.length === 0 ? () => deleteAction(pillar) : null}
                />
              </div>
            </div>

            {/* Slides content — shared between Swiper (desktop) and vertical stack (mobile) */}
            {(() => {
              const statsCard = (
                <div
                  key="stats"
                  style={{
                    background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
                    border: `1px solid ${color}30`,
                    borderRadius: 12,
                    padding: "16px",
                    height: isMobile ? "auto" : "100%",
                    overflowY: "auto",
                    position: "relative",
                  }}
                >
                  {(badges.isBest || badges.isWorst) && (
                    <span
                      style={{
                        position: "absolute", top: 10, right: 10,
                        width: 28, height: 28, borderRadius: 8,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        background: badges.isBest ? "rgba(247,204,83,0.18)" : "rgba(255,45,85,0.12)",
                      }}
                      title={badges.isBest ? "Best overall pillar" : "Worst overall pillar"}
                    >
                      <FontAwesomeIcon
                        icon={badges.isBest ? faTrophy : faArrowTrendDown}
                        style={{ fontSize: 13, color: badges.isBest ? "#c9960a" : "#ff2d55" }}
                      />
                    </span>
                  )}
                  {/* Normalized score */}
                  <div style={{ marginBottom: 14 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color, marginBottom: 4 }}>
                      Score
                    </p>
                    <span style={{ fontSize: 28, fontWeight: 800, color: "#1a2236", lineHeight: 1 }}>{averageScore}</span>
                  </div>

                  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color, marginBottom: 8 }}>
                    {t("Backend.Post.ContentPillars.Total")}
                  </p>
                  <Interactions views={stats.views} likes={stats.likes} comments={stats.comments} />
                  {bestPost && (
                    <>
                      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: "#07bc0c", marginBottom: 6, marginTop: 16 }}>
                        {t("Backend.Post.ContentPillars.BestPost", "Best Post")}
                      </p>
                      <p onClick={() => { setEditPost(bestPost); setShowPostForm(true); }} style={{ fontSize: 12, fontWeight: 400, color: "#038edc", margin: 0, marginBottom: 6, lineHeight: 1.3, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>
                        {bestPost.title || "—"}
                      </p>
                      {bestPost.stats?.length > 0 && (
                        <Interactions views={bestPost.stats.at(-1).views} likes={bestPost.stats.at(-1).likes} comments={bestPost.stats.at(-1).comments} />
                      )}
                      {Number(bestPost.stats?.at(-1)?.score) > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#74788d" }}>Score: {Math.round(Number(bestPost.stats?.at(-1)?.score))}</span>
                      )}
                    </>
                  )}
                  {worstPost && worstPost.id !== bestPost?.id && (
                    <>
                      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: "#ff2d55", marginBottom: 6, marginTop: 16 }}>
                        {t("Backend.Post.ContentPillars.WorstPost", "Worst Post")}
                      </p>
                      <p onClick={() => { setEditPost(worstPost); setShowPostForm(true); }} style={{ fontSize: 12, fontWeight: 400, color: "#038edc", margin: 0, marginBottom: 6, lineHeight: 1.3, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>
                        {worstPost.title || "—"}
                      </p>
                      {worstPost.stats?.length > 0 && (
                        <Interactions views={worstPost.stats.at(-1).views} likes={worstPost.stats.at(-1).likes} comments={worstPost.stats.at(-1).comments} />
                      )}
                      {Number(worstPost.stats?.at(-1)?.score) > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#74788d" }}>Score: {Math.round(Number(worstPost.stats?.at(-1)?.score))}</span>
                      )}
                    </>
                  )}
                </div>
              );

              const postCards = loadingPosts
                ? [1, 2, 3].map((i) => (
                    <div key={`skel-${i}`} style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #f0f0f0", height: isMobile ? "auto" : "100%" }}>
                      <div className="skeleton" style={{ height: 100, borderRadius: 0 }} />
                      <div style={{ padding: "12px 14px" }}>
                        <div className="skeleton" style={{ height: 13, width: "70%", marginBottom: 8 }} />
                        <div className="skeleton" style={{ height: 11, width: "45%" }} />
                      </div>
                    </div>
                  ))
                : posts.map((post, i) => (
                    <Slider
                      key={post.id || i}
                      post={post}
                      posts={posts}
                      setPosts={setPosts}
                      index={i}
                      setEditPost={setEditPost}
                      setShowEdit={setShowPostForm}
                      accentColor={color}
                      averageScore={averageScore}
                    />
                  ));

              const addCard = (
                <div
                  key="add"
                  style={{
                    border: "2px dashed #e0e4ea", borderRadius: 12, padding: "24px 16px",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    height: isMobile ? "auto" : "100%", color: "#adb5bd",
                  }}
                >
                  <p style={{ fontSize: 13, marginBottom: 12 }}>{t("Backend.Post.New")}</p>
                  <AddButton
                    addFunction={async () => {
                      let response = await crudApi.createNew(SocialEndpoints.posts.new, {});
                      if (response.ok) {
                        listHandler.newToList(response, posts, setPosts, null);
                        setEditPostList(posts);
                        let newPost = { ...response.data };
                        newPost.content_pillar = { id: pillar.id };
                        setEditPost(newPost);
                        setShowPostForm(true);
                      }
                    }}
                  />
                </div>
              );

              if (isMobile) {
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {statsCard}
                    {postCards}
                    {addCard}
                  </div>
                );
              }

              return (
                <Swiper
                  modules={[Navigation]}
                  spaceBetween={16}
                  slidesPerView={3}
                  style={{ height: 260 }}
                  navigation={{ prevEl: `.prev-${pillar.id}`, nextEl: `.next-${pillar.id}` }}
                >
                  <SwiperSlide style={{ height: "100%" }}>{statsCard}</SwiperSlide>
                  {postCards.map((card, i) => (
                    <SwiperSlide key={i} style={{ transform: "none", height: "100%" }}>{card}</SwiperSlide>
                  ))}
                  <SwiperSlide style={{ height: "100%" }}>{addCard}</SwiperSlide>
                </Swiper>
              );
            })()}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentPillarTimeline;
